import React, { useMemo } from 'react';
import { BeveledPanel } from '../FabProcessTool/BeveledPanel';
import { Foup } from '../FabProcessTool/Foup';
import {
  getBrushedAluminum,
  getCyanLedMaterial,
  getGraphiteMetal,
  getWhitePaintedMetal,
} from '../FabProcessTool/equipmentMaterials';
import { getProcessEquipmentPortPoses } from '../equipmentPortTransfer';
import { resolveKitFoupScale } from '../../types';
import type { FabToolDetail } from '../FabProcessTool/types';
import type { Awp300Layout } from './awp300Layout';

export type Awp300LoadPortsProps = {
  layout: Awp300Layout;
  portCount?: number;
  occupiedPorts?: number[];
  portFractions?: readonly number[];
  opacity?: number;
  detail?: FabToolDetail;
};

/** Dual front load-port docks aligned with first internal rack tier. */
export const Awp300LoadPorts: React.FC<Awp300LoadPortsProps> = ({
  layout,
  portCount = 2,
  occupiedPorts = [],
  portFractions,
  opacity = 1,
  detail = 'high',
}) => {
  const graphite = useMemo(() => getGraphiteMetal(opacity), [opacity]);
  const white = useMemo(() => getWhitePaintedMetal(opacity), [opacity]);
  const brushed = useMemo(() => getBrushedAluminum(opacity), [opacity]);
  const cyan = useMemo(() => getCyanLedMaterial(1, opacity), [opacity]);

  const { width, height, depth, loadDeckY } = layout;
  const foupScale = resolveKitFoupScale(width, height, depth);
  const poses = getProcessEquipmentPortPoses(
    width,
    height,
    depth,
    portCount,
    portFractions
  );
  const showFoup = detail !== 'low' && detail !== 'very-low';
  const padH = height * 0.022;
  const padD = depth * 0.14;
  const padW = width * 0.18;

  return (
    <group name="Awp300LoadPorts">
      {poses.map((pose, i) => {
        const occupied = occupiedPorts.includes(i);
        const [px, , pz] = pose.loadPort;
        const y = loadDeckY;
        return (
          <group key={`port-${i}`} position={[px, y, pz]}>
            <BeveledPanel
              size={[padW, padH, padD]}
              material={graphite}
              castShadow
              receiveShadow
            />
            <BeveledPanel
              size={[padW * 0.92, padH * 0.35, padD * 0.85]}
              material={brushed}
              position={[0, padH * 0.32, padD * 0.02]}
            />
            <BeveledPanel
              size={[padW * 0.88, padH * 0.22, padH * 0.6]}
              material={white}
              position={[0, padH * 0.55, -padD * 0.42]}
            />
            <BeveledPanel
              size={[padW * 0.08, padH * 0.5, padH * 0.08]}
              material={cyan}
              position={[padW * 0.42, padH * 0.45, -padD * 0.38]}
            />
            {showFoup && occupied ? (
              <Foup scale={foupScale} opacity={opacity} position={[0, padH, -padD * 0.15]} />
            ) : null}
          </group>
        );
      })}
    </group>
  );
};

export default Awp300LoadPorts;
